import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";

import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import LeftTable from "../../../../components/LeftTable";
import Modal from "../../../../components/Modal";
import { useAuth } from "../../../../context/auth-context";
import { generatePdfReport } from "../../../../utils/pdf/Report-Standby-Technical";
import { generatePdfReportAllOS } from "../../../../utils/pdf/Report-Standby-All-OS";

type Row = (string | number | React.ReactNode)[];

type TipoStandby = "servico" | "venda";
type AcaoStandby = "retornar" | "baixar";

type DepartamentoUsuario =
  | "diretoria"
  | "coordenacao"
  | "suprimentos"
  | "outro";

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

type StandbyItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;

  quantidade: number | string;
  quantidade_origem?: number | string | null;
  quantidade_retornada?: number | string | null;
  quantidade_baixada?: number | string | null;

  custo_unitario: string | number | null;
  custo_unitario_atual?: string | number | null;
  margem_percentual?: string | number | null;
  preco_venda?: string | number | null;
  moeda?: string | null;

  saida_mov_ids?: Array<number | string>;
  retorno_mov_ids?: Array<number | string>;

  pendente_retorno?: boolean;
  pendente_responsavel_id?: number | string | null;
  pendente_responsavel_nome?: string | null;
  pendente_criado_em?: string | null;

  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    posicao?: string | null;
    ncm?: string | null;
    classificacao?: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

type StandbyMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;

    tipo: string | null;
    tipo_standby?: TipoStandby | string | null;

    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    criado_em: string;
    os_numero?: string | null;
    proposta_venda?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;
    cliente_nome?: string | null;
    empresa_cliente?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    origem_mov_id?: number | string | null;

    coordenador_responsavel_ids?: Array<number | string> | null;
    coordenadores_responsaveis_ids?: Array<number | string> | null;
    coordenadores_responsaveis_nomes?: string[] | null;

    comentario?: string | null;

    pendente_retorno?: boolean;
    pendente_id?: number | string | null;
    pendente_responsavel_id?: number | string | null;
    pendente_responsavel_nome?: string | null;
    pendente_criado_em?: string | null;

    empresa?: {
      id?: number | string | null;
      nome_fantasia?: string | null;
      responsaveis_ids?: Array<number | string>;
      responsaveis_nomes?: string[];
    };

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };

  itens: StandbyItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta?: {
    filtros: {
      tipo_standby?: TipoStandby | string | null;
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: StandbyMovAPI[];
};

type StandbyRowMeta = {
  key: string;
  movimentacaoId: number;
  itemId: number;
  produtoId: number;
  referencia: string;
  tipoStandby: TipoStandby;
  codigo: string;
  nome: string;
  quantidade: number;
  custoUnitario: number;
  moeda: string;
  responsavelId: number | null;
  responsavelNome: string;
  tecnicoNome: string;
  coordenadorNome: string;
  empresa: string;
  embarcacao: string;
  data: string;
  hora: string;
  rawMov: StandbyMovAPI;
  rawItem: StandbyItemAPI;
};

type MovimentarStandbyPayload = {
  modulo_id: number;
  tipo_standby: TipoStandby;
  acao: AcaoStandby;
  itens: Array<{
    movimentacao_id: number;
    produto_id: number;
    item_id?: number;
    quantidade: number;
  }>;
};

type MovimentarStandbyResp = {
  ok: boolean;
  trace_id?: string;
  erro?: string;
  erros?: string[];
  data?: unknown;
};

type QuantityDialogState = {
  acao: AcaoStandby;
  modo: "item" | "todos";
  metas: StandbyRowMeta[];
  titulo: string;
  descricao: string;
  quantidadeMaxima: number;
};

const URL_STANDBY =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-standby";

const URL_STANDBY_MOVIMENTAR =
  "https://api.logvi-havox.com/template/estoque-administrativo/post-standby/movimentar";

const ACCESS_TOKEN_KEY = "access_token";
const POLL_FALLBACK_MS = 15000;
const PRIMARY_COLOR = "#0d343d";

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "materiais-para-venda": "materiais-para-venda",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

const IDX_SERVICO = {
  REF: 0,
  COORDENADOR: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  MOEDA: 6,
  TECNICO: 7,
  DATA: 8,
  HORA: 9,
  ATRASO: 10,
};

const IDX_VENDA = {
  REF: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  MOEDA: 6,
  RESPONSAVEL: 7,
  DATA: 8,
  HORA: 9,
  ATRASO: 10,
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function jitter(ms: number) {
  return ms + Math.floor(Math.random() * 400);
}

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function normalizeDepartamento(value: unknown): string {
  return normalizeComparable(value);
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function parseNumber(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const n = Number(String(value ?? "0").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseBRL(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value ?? "").trim();
  if (!raw) return 0;

  const cleaned = raw
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "")
    .replace(",", ".");

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function normalizarQuantidadeOperacao(value: unknown): number {
  const n = Number(String(value ?? "").replace(",", "."));

  if (!Number.isFinite(n)) return NaN;

  return Math.trunc(n);
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  const symbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "BRL"
      ? "R$"
      : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatFirstTwoNames(nome: string | null | undefined): string {
  if (!nome) return "-";

  const parts = String(nome).trim().split(/\s+/).filter(Boolean);

  if (parts.length <= 2) return parts.join(" ");

  return `${parts[0]} ${parts[1]}`;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function toStartOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseDateSmart(s: string): Date | null {
  if (!s) return null;

  const t = s.trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(t);

  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]) - 1;
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, mm, dd);

    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d2 = new Date(t);
  return Number.isNaN(d2.getTime()) ? null : d2;
}

function diffDaysFromToday(dateStr: string): number | null {
  const d = parseDateSmart(dateStr);

  if (!d) return null;

  const today = toStartOfDay(new Date());
  const start = toStartOfDay(d);
  const ms = today.getTime() - start.getTime();

  return Math.max(0, Math.floor(ms / 86400000));
}

function toNumberId(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) ? n : 0;
}

function isNA(value: unknown): boolean {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return !s || s === "n/a" || s === "null" || s === "undefined";
}

function getReferencia(mov: StandbyMovAPI["movimentacao"], tipo: TipoStandby) {
  const os = String(mov.os_numero ?? "").trim();
  const pv = String(mov.proposta_venda ?? "").trim();

  if (tipo === "venda") {
    if (!isNA(pv)) return pv;
    if (!isNA(os)) return os;
    return "-";
  }

  if (!isNA(os)) return os;
  if (!isNA(pv)) return pv;
  return "-";
}

function getTecnico(mov: StandbyMovAPI["movimentacao"]): string {
  return (
    String(mov.coletor_nome ?? "").trim() ||
    String(mov.responsavel_nome ?? "").trim() ||
    "-"
  );
}

function getResponsavelRetirada(mov: StandbyMovAPI["movimentacao"]): string {
  return (
    String(mov.responsavel_nome ?? "").trim() ||
    String(mov.coletor_nome ?? "").trim() ||
    "-"
  );
}

function cleanNames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((v) => String(v ?? "").trim())
    .filter((v) => v && v.toLowerCase() !== "pendente");
}

function cleanNumberIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v > 0);
}

function uniqueNumbers(values: number[]): number[] {
  return Array.from(
    new Set(values.filter((v) => Number.isInteger(v) && v > 0))
  );
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)));
}

function firstTwoComparableName(value: unknown): string {
  const parts = String(value ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return normalizeComparable(parts.slice(0, 2).join(" "));
}

function getSessaoUsuarioIds(sessao: any): number[] {
  return uniqueNumbers([
    toNumberId(sessao?.uid),
    toNumberId(sessao?.id),
    toNumberId(sessao?.usuario_id),
    toNumberId(sessao?.usuarioId),
    toNumberId(sessao?.solicitante_id),
    toNumberId(sessao?.usuario?.id),
    toNumberId(sessao?.usuario?.usuario_id),
    toNumberId(sessao?.colaborador?.id),
    toNumberId(sessao?.colaborador?.usuario_id),
  ]);
}

function getSessaoUsuarioNomes(sessao: any): string[] {
  return uniqueStrings([
    String(sessao?.nome ?? ""),
    String(sessao?.nome_completo ?? ""),
    String(sessao?.usuario_nome ?? ""),
    String(sessao?.usuario?.nome ?? ""),
    String(sessao?.usuario?.nome_completo ?? ""),
    String(sessao?.colaborador?.nome ?? ""),
    String(sessao?.colaborador?.nome_completo ?? ""),
  ]);
}

function getCoordenadorIds(
  mov?: StandbyMovAPI["movimentacao"] | null
): number[] {
  if (!mov) return [];

  return uniqueNumbers([
    ...cleanNumberIds(mov.coordenadores_responsaveis_ids),
    ...cleanNumberIds(mov.coordenador_responsavel_ids),
    ...cleanNumberIds(mov.empresa?.responsaveis_ids),
  ]);
}

function getCoordenadorNomes(
  mov?: StandbyMovAPI["movimentacao"] | null
): string[] {
  if (!mov) return [];

  return uniqueStrings([
    ...cleanNames(mov.coordenadores_responsaveis_nomes),
    ...cleanNames(mov.empresa?.responsaveis_nomes),
  ]);
}

function arraysOverlap(a: number[], b: number[]): boolean {
  if (!a.length || !b.length) return false;

  const set = new Set(a);
  return b.some((value) => set.has(value));
}

function namesOverlap(a: string[], b: string[]): boolean {
  if (!a.length || !b.length) return false;

  const normalizedA = new Set(a.map(normalizeComparable).filter(Boolean));
  const firstTwoA = new Set(a.map(firstTwoComparableName).filter(Boolean));

  return b.some((name) => {
    const normalized = normalizeComparable(name);
    const firstTwo = firstTwoComparableName(name);

    return (
      (!!normalized && normalizedA.has(normalized)) ||
      (!!firstTwo && firstTwoA.has(firstTwo))
    );
  });
}

function isMinhaOSMeta(
  meta: StandbyRowMeta | undefined | null,
  usuarioIds: number[],
  usuarioNomes: string[]
): boolean {
  if (!meta || meta.tipoStandby !== "servico") return false;

  const mov = meta.rawMov?.movimentacao;
  const coordenadorIds = getCoordenadorIds(mov);

  if (arraysOverlap(coordenadorIds, usuarioIds)) {
    return true;
  }

  const coordenadorNomes = getCoordenadorNomes(mov);

  return namesOverlap(coordenadorNomes, usuarioNomes);
}

function getCoordenador(mov?: StandbyMovAPI["movimentacao"] | null): string {
  if (!mov) return "-";

  const nomesDiretos = cleanNames(mov?.coordenadores_responsaveis_nomes);

  if (nomesDiretos.length > 0) {
    return nomesDiretos.join(", ");
  }

  const nomesEmpresa = cleanNames(mov?.empresa?.responsaveis_nomes);

  if (nomesEmpresa.length > 0) {
    return nomesEmpresa.join(", ");
  }

  const ids = Array.isArray(mov?.coordenadores_responsaveis_ids)
    ? mov.coordenadores_responsaveis_ids
        .map((id) => String(id ?? "").trim())
        .filter(Boolean)
    : [];

  if (ids.length > 0) {
    return `IDs: ${ids.join(", ")}`;
  }

  return "-";
}

function getEmpresa(mov: StandbyMovAPI["movimentacao"]): string {
  return (
    String(mov.empresa?.nome_fantasia ?? "").trim() ||
    String(mov.empresa_cliente ?? "").trim() ||
    String(mov.cliente_nome ?? "").trim() ||
    "-"
  );
}

function getEmbarcacao(mov: StandbyMovAPI["movimentacao"]): string {
  return String(mov.embarcacao ?? "").trim() || "-";
}

function resolveTipoFromPath(pathname: string): TipoStandby {
  const path = pathname.toLowerCase();

  if (path.includes("/standby/standby-comercial")) return "venda";
  if (path.includes("/standby-comercial")) return "venda";

  return "servico";
}

function resolveDepartamentoUsuario(sessao: any): DepartamentoUsuario {
  const candidatos = [
    sessao?.departamento,
    sessao?.departamento_nome,
    sessao?.departamento_slug,
    sessao?.departamento_usuario,
    sessao?.usuario_departamento,
    sessao?.usuario?.departamento,
    sessao?.usuario?.departamento_nome,
    sessao?.usuario?.departamento_slug,
    sessao?.colaborador?.departamento,
    sessao?.colaborador?.departamento_nome,
    sessao?.colaborador?.departamento_slug,
    sessao?.perfil,
    sessao?.perfil_nome,
    sessao?.cargo,
    sessao?.cargo_nome,
  ];

  for (const candidato of candidatos) {
    const dep = normalizeDepartamento(candidato);

    if (!dep) continue;

    if (dep.includes("diretoria") || dep.includes("diretor")) {
      return "diretoria";
    }

    if (
      dep.includes("coordenacao") ||
      dep.includes("coordenador") ||
      dep.includes("coord")
    ) {
      return "coordenacao";
    }

    if (dep.includes("suprimentos") || dep.includes("compras")) {
      return "suprimentos";
    }
  }

  const departamentosAcessiveis: string[] = [];

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  for (const endereco of enderecos) {
    const deps = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of deps) {
      departamentosAcessiveis.push(
        normalizeDepartamento(
          dep?.slug ?? dep?.routeKey ?? dep?.nome ?? dep?.label ?? dep?.codigo
        )
      );
    }
  }

  if (
    departamentosAcessiveis.some(
      (dep) => dep.includes("diretoria") || dep.includes("diretor")
    )
  ) {
    return "diretoria";
  }

  if (
    departamentosAcessiveis.some(
      (dep) =>
        dep.includes("coordenacao") ||
        dep.includes("coordenador") ||
        dep.includes("coord")
    )
  ) {
    return "coordenacao";
  }

  if (
    departamentosAcessiveis.some(
      (dep) => dep.includes("suprimentos") || dep.includes("compras")
    )
  ) {
    return "suprimentos";
  }

  return "outro";
}

function getPermissoesStandby(departamento: DepartamentoUsuario) {
  return {
    podeGerarRelatorio: true,
    podeRetornar:
      departamento === "diretoria" || departamento === "suprimentos",
    podeBaixar: departamento === "diretoria" || departamento === "coordenacao",
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  areaSlug?: string,
  estoqueSlug?: string
): ModuloAtual | null {
  const area = normalizeComparable(areaSlug || "suprimentos");
  const estoque = normalizeComparable(
    estoqueSlug || "estoque-p-servico-e-comercial"
  );

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoque] || estoque;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (area && depSlug && depSlug !== area) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoque ||
            modLabel === estoque ||
            modCodigo.includes(estoque) ||
            estoque.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

function buildRawByReferencia(rawMovs: StandbyMovAPI[], tipo: TipoStandby) {
  const index: Record<
    string,
    {
      mov: StandbyMovAPI["movimentacao"];
      itens: StandbyItemAPI[];
    }
  > = {};

  for (const mov of rawMovs || []) {
    const movimento = mov?.movimentacao;

    if (!movimento) continue;

    const ref = getReferencia(movimento, tipo);

    if (!index[ref]) {
      index[ref] = {
        mov: movimento,
        itens: [],
      };
    }

    index[ref].itens.push(...(mov.itens || []));
  }

  return index;
}

function flattenStandby(
  movs: StandbyMovAPI[],
  tipo: TipoStandby
): {
  rows: Row[];
  rowMeta: StandbyRowMeta[];
} {
  const rows: Row[] = [];
  const rowMeta: StandbyRowMeta[] = [];

  for (const mov of movs || []) {
    const movimento = mov?.movimentacao;

    if (!movimento) continue;

    const { data, hora } = toBRT(movimento.criado_em);

    const referencia = getReferencia(movimento, tipo);
    const coordenador = getCoordenador(movimento);
    const tecnico = getTecnico(movimento);
    const responsavelRetirada = getResponsavelRetirada(movimento);
    const empresa = getEmpresa(movimento);
    const embarcacao = getEmbarcacao(movimento);

    for (const item of mov.itens || []) {
      const quantidade = parseNumber(item.quantidade);
      const custo = parseNumber(item.custo_unitario);
      const moeda = String(item.moeda || "BRL").toUpperCase();
      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nome = String(item.produto?.nome ?? "").toUpperCase();
      const dias = diffDaysFromToday(data);

      if (quantidade <= 0) continue;

      if (tipo === "venda") {
        rows.push([
          referencia,
          "Venda",
          codigo,
          nome,
          String(quantidade),
          formatMoney(custo, moeda),
          moeda,
          responsavelRetirada,
          data,
          hora,
          dias == null ? "-" : String(dias),
        ]);
      } else {
        rows.push([
          referencia,
          coordenador,
          codigo,
          nome,
          String(quantidade),
          formatMoney(custo, moeda),
          moeda,
          formatFirstTwoNames(tecnico),
          data,
          hora,
          dias == null ? "-" : String(dias),
        ]);
      }

      rowMeta.push({
        key: `${movimento.id}:${item.id}:${codigo}`,
        movimentacaoId: toNumberId(movimento.id),
        itemId: toNumberId(item.id),
        produtoId: toNumberId(item.produto_id ?? item.produto?.id),
        referencia,
        tipoStandby: tipo,
        codigo,
        nome,
        quantidade,
        custoUnitario: custo,
        moeda,
        responsavelId:
          movimento.responsavel_id == null
            ? null
            : toNumberId(movimento.responsavel_id),
        responsavelNome: responsavelRetirada,
        tecnicoNome: tecnico,
        coordenadorNome: coordenador,
        empresa,
        embarcacao,
        data,
        hora,
        rawMov: mov,
        rawItem: item,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

async function fetchStandbyOnce(
  moduloAtual: ModuloAtual | null,
  tipo: TipoStandby
): Promise<{
  rows: Row[];
  rowMeta: StandbyRowMeta[];
  rawMovs: StandbyMovAPI[];
  rawByReferencia: Record<
    string,
    { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
  >;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota.");
  }

  const payload = {
    modulo_id: moduloAtual.id,
    tipo_standby: tipo,
    order: "desc",
    limit: 10000,
    offset: 0,
  };

  const res = await fetch(URL_STANDBY, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || json?.ok === false) {
    const erro =
      (json as any)?.erro ||
      (Array.isArray((json as any)?.erros) &&
        (json as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const rawMovs = json && Array.isArray(json.data) ? json.data : [];
  const flat = flattenStandby(rawMovs, tipo);

  return {
    rows: flat.rows,
    rowMeta: flat.rowMeta,
    rawMovs,
    rawByReferencia: buildRawByReferencia(rawMovs, tipo),
    resp: json,
  };
}

async function movimentarStandby(
  payload: MovimentarStandbyPayload
): Promise<MovimentarStandbyResp> {
  const res = await fetch(URL_STANDBY_MOVIMENTAR, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const parsed = (await res
    .json()
    .catch(() => null)) as MovimentarStandbyResp | null;

  const safeParsed: MovimentarStandbyResp = parsed ?? { ok: res.ok };

  if (!res.ok) {
    const erro =
      safeParsed.erro ||
      (Array.isArray(safeParsed.erros) && safeParsed.erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  if (safeParsed.ok === false) {
    const erro =
      safeParsed.erro ||
      (Array.isArray(safeParsed.erros) && safeParsed.erros.join(" | ")) ||
      "Falha ao movimentar standby.";

    throw new Error(erro);
  }

  return safeParsed;
}

function useStandbyGenericoData(
  moduloAtual: ModuloAtual | null,
  tipo: TipoStandby,
  authLoading: boolean
) {
  const [rows, setRows] = useState<Row[]>([]);
  const [rowMeta, setRowMeta] = useState<StandbyRowMeta[]>([]);
  const [rawMovs, setRawMovs] = useState<StandbyMovAPI[]>([]);
  const [rawByReferencia, setRawByReferencia] = useState<
    Record<
      string,
      { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
    >
  >({});
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchStandbyOnce(moduloAtual, tipo);

      setRows(result.rows);
      setRowMeta(result.rowMeta);
      setRawMovs(result.rawMovs);
      setRawByReferencia(result.rawByReferencia);
      setResp(result.resp);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar standby.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setRawMovs([]);
    setRawByReferencia({});
    setResp(null);

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useStandbyGenericoData loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, tipo, authLoading]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    rawMovs,
    rawByReferencia,
    resp,
    loading: authLoading || loading,
    erro,
    refresh,
  };
}

function buildPayloadFromMetas(params: {
  moduloAtual: ModuloAtual | null;
  tipo: TipoStandby;
  acao: AcaoStandby;
  metas: StandbyRowMeta[];
  quantidade: number;
}): MovimentarStandbyPayload {
  const { moduloAtual, tipo, acao, metas, quantidade } = params;

  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado.");
  }

  if (!Number.isInteger(quantidade) || quantidade <= 0) {
    throw new Error("Informe uma quantidade inteira maior que zero.");
  }

  const itens = metas
    .map((meta) => {
      if (quantidade > meta.quantidade) {
        throw new Error(
          `Quantidade informada para o produto ${meta.codigo} é maior que o saldo em standby (${meta.quantidade}).`
        );
      }

      return {
        movimentacao_id: meta.movimentacaoId,
        produto_id: meta.produtoId,
        item_id: meta.itemId,
        quantidade,
      };
    })
    .filter(
      (item) =>
        Number.isInteger(item.movimentacao_id) &&
        item.movimentacao_id > 0 &&
        Number.isInteger(item.produto_id) &&
        item.produto_id > 0 &&
        item.quantidade > 0
    );

  if (!itens.length) {
    throw new Error("Nenhum item válido selecionado para movimentação.");
  }

  return {
    modulo_id: moduloAtual.id,
    tipo_standby: tipo,
    acao,
    itens,
  };
}

const StandbyGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug?: string;
    estoqueSlug?: string;
  }>();

  const location = useLocation();
  const { sessao, loading: authLoading } = useAuth();

  const departamentoUsuario = useMemo(
    () => resolveDepartamentoUsuario(sessao),
    [sessao]
  );

  const permissoesStandby = useMemo(
    () => getPermissoesStandby(departamentoUsuario),
    [departamentoUsuario]
  );

  const usuarioIds = useMemo(() => getSessaoUsuarioIds(sessao), [sessao]);

  const usuarioNomes = useMemo(() => getSessaoUsuarioNomes(sessao), [sessao]);

  const tipo = useMemo<TipoStandby>(
    () => resolveTipoFromPath(location.pathname),
    [location.pathname]
  );

  const moduloAtual = useMemo(
    () =>
      findModuloAtualFromSessao(
        sessao,
        areaSlug || "suprimentos",
        estoqueSlug || "estoque-p-servico-e-comercial"
      ),
    [sessao, areaSlug, estoqueSlug]
  );

  const {
    rows,
    rowMeta,
    rawMovs,
    rawByReferencia,
    resp,
    loading,
    erro,
    refresh,
  } = useStandbyGenericoData(moduloAtual, tipo, authLoading);

  const deveAgruparMinhasOS = useMemo(() => {
    return (
      tipo === "servico" && (usuarioIds.length > 0 || usuarioNomes.length > 0)
    );
  }, [tipo, usuarioIds, usuarioNomes]);

  const minhasReferencias = useMemo(() => {
    const refs = new Set<string>();

    rowMeta.forEach((meta) => {
      if (isMinhaOSMeta(meta, usuarioIds, usuarioNomes)) {
        refs.add(meta.referencia);
      }
    });

    return refs;
  }, [rowMeta, usuarioIds, usuarioNomes]);

  const groupedStandby = useMemo(() => {
    const zipped = rows.map((row, index) => ({
      row,
      meta: rowMeta[index],
      index,
    }));

    if (!deveAgruparMinhasOS || tipo !== "servico" || zipped.length === 0) {
      return {
        rows: zipped.map((entry) => entry.row),
        rowMeta: zipped.map((entry) => entry.meta),
      };
    }

    type RefGroup = {
      referencia: string;
      entries: typeof zipped;
      firstIndex: number;
      isMine: boolean;
    };

    const groups = new Map<string, RefGroup>();

    zipped.forEach((entry) => {
      const referencia =
        String(
          entry.meta?.referencia ?? entry.row[IDX_SERVICO.REF] ?? "-"
        ).trim() || "-";

      const current = groups.get(referencia);

      if (current) {
        current.entries.push(entry);
        current.isMine =
          current.isMine || isMinhaOSMeta(entry.meta, usuarioIds, usuarioNomes);
        return;
      }

      groups.set(referencia, {
        referencia,
        entries: [entry],
        firstIndex: entry.index,
        isMine: isMinhaOSMeta(entry.meta, usuarioIds, usuarioNomes),
      });
    });

    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (a.isMine !== b.isMine) return a.isMine ? -1 : 1;
      return a.firstIndex - b.firstIndex;
    });

    const entries = ordered.flatMap((group) => group.entries);

    return {
      rows: entries.map((entry) => entry.row),
      rowMeta: entries.map((entry) => entry.meta),
    };
  }, [rows, rowMeta, deveAgruparMinhasOS, tipo, usuarioIds, usuarioNomes]);

  const displayRows = groupedStandby.rows;
  const displayRowMeta = groupedStandby.rowMeta;

  const getRowGroupForTable = useCallback(
    (rowData: Row) => {
      if (!deveAgruparMinhasOS || tipo !== "servico") return "others";

      const referencia = String(rowData?.[IDX_SERVICO.REF] ?? "").trim();

      if (!referencia) return "others";

      return minhasReferencias.has(referencia) ? "mine" : "others";
    },
    [deveAgruparMinhasOS, tipo, minhasReferencias]
  );

  const headers = useMemo(() => {
    if (tipo === "venda") {
      return [
        "PV",
        "Tipo",
        "Código",
        "Nome do Produto",
        "Quantidade",
        "Custo Unitário",
        "Moeda",
        "R. retirada",
        "Data",
        "Hora",
        "Dias de Atraso",
      ];
    }

    return [
      "OS",
      "C. Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Moeda",
      "Técnico",
      "Data",
      "Hora",
      "Dias de Atraso",
    ];
  }, [tipo]);

  const columnWidths = useMemo(() => {
    if (tipo === "venda") {
      return [
        "70px",
        "80px",
        "90px",
        "300px",
        "80px",
        "120px",
        "70px",
        "140px",
        "90px",
        "70px",
        "80px",
      ];
    }

    return [
      "70px",
      "140px",
      "90px",
      "300px",
      "80px",
      "120px",
      "70px",
      "130px",
      "90px",
      "70px",
      "80px",
    ];
  }, [tipo]);

  const [openRef, setOpenRef] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submittingAction, setSubmittingAction] = useState<AcaoStandby | null>(
    null
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const [quantityDialog, setQuantityDialog] =
    useState<QuantityDialogState | null>(null);
  const [quantityInput, setQuantityInput] = useState<string>("");
  const [quantityError, setQuantityError] = useState<string | null>(null);

  const closeModal = useCallback(() => {
    if (submittingAction) return;

    setOpenRef(null);
    setSelectedIndex(null);
    setActionError(null);
    setQuantityDialog(null);
    setQuantityInput("");
    setQuantityError(null);
  }, [submittingAction]);

  const handleDoubleClick = useCallback(
    (viewIndex: number, rowData?: Row, originalIndex?: number) => {
      const idx = originalIndex ?? viewIndex;
      const row = (displayRows[idx] ?? rowData) as Row | undefined;

      if (!row) return;

      const ref = String(row[0] ?? "").trim();

      if (!ref) return;

      setOpenRef(ref);
      setSelectedIndex(null);
      setActionError(null);
      setQuantityDialog(null);
      setQuantityInput("");
      setQuantityError(null);
    },
    [displayRows]
  );

  const pageTitle = useMemo(() => {
    const area = slugToTitle(normalizeSlug(areaSlug || "suprimentos"));
    const estoqueNome = resp?.estoque?.nome || moduloAtual?.label;

    const tipoLabel =
      tipo === "venda" ? "Standby Comercial" : "Standby Serviço";

    if (estoqueNome) {
      return `${area} - ${tipoLabel} - ${estoqueNome}`;
    }

    return `${area} - ${tipoLabel}`;
  }, [areaSlug, resp, moduloAtual, tipo]);

  const filteredModalRows = useMemo(() => {
    if (!openRef) return [];

    return displayRows
      .map((row, index) => ({
        row,
        meta: displayRowMeta[index],
        originalIndex: index,
      }))
      .filter((entry) => String(entry.row[0] ?? "").trim() === openRef);
  }, [openRef, displayRows, displayRowMeta]);

  const modalRawMovs = useMemo(() => {
    if (!openRef) return [];

    return rawMovs.filter((mov) => {
      const movimento = mov?.movimentacao;
      if (!movimento) return false;
      return getReferencia(movimento, tipo) === openRef;
    });
  }, [openRef, rawMovs, tipo]);

  const modalMetas = useMemo(() => {
    return filteredModalRows
      .map((entry) => entry.meta)
      .filter(Boolean) as StandbyRowMeta[];
  }, [filteredModalRows]);

  const selectedMeta = useMemo(() => {
    if (selectedIndex == null) return null;

    return filteredModalRows[selectedIndex]?.meta ?? null;
  }, [filteredModalRows, selectedIndex]);

  const referenciaAbertaEhMinhaOS = useMemo(() => {
    if (!openRef || tipo !== "servico") return false;
    return minhasReferencias.has(openRef);
  }, [openRef, tipo, minhasReferencias]);

  const podeBaixarReferenciaAberta = useMemo(() => {
    if (!openRef) return false;

    if (departamentoUsuario === "diretoria") {
      return permissoesStandby.podeBaixar;
    }

    if (departamentoUsuario === "coordenacao") {
      return (
        permissoesStandby.podeBaixar &&
        tipo === "servico" &&
        referenciaAbertaEhMinhaOS
      );
    }

    return false;
  }, [
    openRef,
    departamentoUsuario,
    permissoesStandby.podeBaixar,
    tipo,
    referenciaAbertaEhMinhaOS,
  ]);

  const modalMetasParaBaixa = useMemo(() => {
    return podeBaixarReferenciaAberta ? modalMetas : [];
  }, [podeBaixarReferenciaAberta, modalMetas]);

  const selectedMetaParaBaixa = useMemo(() => {
    return podeBaixarReferenciaAberta ? selectedMeta : null;
  }, [podeBaixarReferenciaAberta, selectedMeta]);

  const modalInfo = useMemo(() => {
    if (!openRef) return [];

    const first = filteredModalRows[0]?.meta;
    const rawEntry = rawByReferencia[openRef];

    const totalMateriais = filteredModalRows.length;
    const totalUnidades = filteredModalRows.reduce(
      (acc, item) => acc + parseNumber(item.meta?.quantidade ?? item.row[4]),
      0
    );
    const totalCusto = filteredModalRows.reduce((acc, item) => {
      const qtd = parseNumber(item.meta?.quantidade ?? item.row[4]);
      const custo = parseNumber(
        item.meta?.custoUnitario ?? parseBRL(item.row[5])
      );
      return acc + qtd * custo;
    }, 0);

    if (tipo === "venda") {
      return [
        { label: "Número da P.V.", value: openRef },
        {
          label: "R. retirada",
          value:
            first?.responsavelNome ||
            rawEntry?.mov?.responsavel_nome ||
            rawEntry?.mov?.coletor_nome ||
            "-",
        },
        {
          label: "Empresa",
          value: first?.empresa || rawEntry?.mov?.empresa_cliente || "-",
        },
        {
          label: "Embarcação",
          value: first?.embarcacao || rawEntry?.mov?.embarcacao || "-",
        },
        { label: "Data", value: first?.data || "-" },
        { label: "Hora", value: first?.hora || "-" },
        {
          label: "Total de Materiais em Standby",
          value: `${totalMateriais} itens, ${totalUnidades} unidades`,
        },
        {
          label: "Custo Total (aprox.)",
          value:
            totalCusto > 0
              ? totalCusto.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })
              : "-",
        },
      ];
    }

    return [
      { label: "Número da O.S.", value: openRef },
      {
        label: "C. Responsável",
        value:
          first?.coordenadorNome ||
          getCoordenador(rawEntry?.mov as StandbyMovAPI["movimentacao"]) ||
          "-",
      },
      {
        label: "Responsável/Técnico",
        value:
          first?.tecnicoNome ||
          rawEntry?.mov?.coletor_nome ||
          rawEntry?.mov?.responsavel_nome ||
          "-",
      },
      {
        label: "Empresa",
        value: first?.empresa || rawEntry?.mov?.empresa_cliente || "-",
      },
      {
        label: "Embarcação",
        value: first?.embarcacao || rawEntry?.mov?.embarcacao || "-",
      },
      { label: "Data", value: first?.data || "-" },
      { label: "Hora", value: first?.hora || "-" },
      {
        label: "Total de Materiais Coletados",
        value: `${totalMateriais} itens, ${totalUnidades} unidades`,
      },
      {
        label: "Custo Total (aprox.)",
        value:
          totalCusto > 0
            ? totalCusto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            : "-",
      },
    ];
  }, [openRef, filteredModalRows, rawByReferencia, tipo]);

  const modalHeaders = useMemo(() => {
    if (tipo === "venda") {
      return [
        "Tipo",
        "Código",
        "Nome do Produto",
        "Quantidade",
        "Custo Unitário",
        "Moeda",
        "R. retirada",
        "Data",
        "Hora",
      ];
    }

    return [
      "C. Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Moeda",
      "Técnico",
      "Data",
      "Hora",
    ];
  }, [tipo]);

  const modalColWidths = useMemo(() => {
    if (tipo === "venda") {
      return [
        "80px",
        "100px",
        "360px",
        "90px",
        "120px",
        "70px",
        "180px",
        "90px",
        "70px",
      ];
    }

    return [
      "150px",
      "100px",
      "360px",
      "90px",
      "120px",
      "70px",
      "180px",
      "90px",
      "70px",
    ];
  }, [tipo]);

  const modalTableRows = useMemo(() => {
    return filteredModalRows.map(({ row }) => {
      if (tipo === "venda") {
        return [
          String(row[IDX_VENDA.TIPO] ?? "-"),
          String(row[IDX_VENDA.CODIGO] ?? "-"),
          String(row[IDX_VENDA.NOME] ?? "-"),
          String(row[IDX_VENDA.QTD] ?? "-"),
          String(row[IDX_VENDA.CUSTO] ?? "-"),
          String(row[IDX_VENDA.MOEDA] ?? "-"),
          String(row[IDX_VENDA.RESPONSAVEL] ?? "-"),
          String(row[IDX_VENDA.DATA] ?? "-"),
          String(row[IDX_VENDA.HORA] ?? "-"),
        ];
      }

      return [
        String(row[IDX_SERVICO.COORDENADOR] ?? "-"),
        String(row[IDX_SERVICO.CODIGO] ?? "-"),
        String(row[IDX_SERVICO.NOME] ?? "-"),
        String(row[IDX_SERVICO.QTD] ?? "-"),
        String(row[IDX_SERVICO.CUSTO] ?? "-"),
        String(row[IDX_SERVICO.MOEDA] ?? "-"),
        String(row[IDX_SERVICO.TECNICO] ?? "-"),
        String(row[IDX_SERVICO.DATA] ?? "-"),
        String(row[IDX_SERVICO.HORA] ?? "-"),
      ];
    });
  }, [filteredModalRows, tipo]);

  const centerColsModal = useMemo(() => {
    if (tipo === "venda") {
      return [0, 1, 3, 4, 5, 7, 8];
    }

    return [1, 3, 4, 5, 7, 8];
  }, [tipo]);

  const abrirPopupQuantidade = useCallback(
    (acao: AcaoStandby, modo: "item" | "todos") => {
      if (!moduloAtual?.id) {
        setActionError("Módulo de estoque não identificado.");
        return;
      }

      if (acao === "retornar" && !permissoesStandby.podeRetornar) {
        setActionError(
          "Seu departamento não possui permissão para retornar standby."
        );
        return;
      }

      if (acao === "baixar" && !podeBaixarReferenciaAberta) {
        setActionError(
          departamentoUsuario === "coordenacao"
            ? "Você só pode dar baixa nas O.S. vinculadas ao seu usuário."
            : "Seu departamento não possui permissão para baixar standby."
        );
        return;
      }

      const metas =
        modo === "item" ? (selectedMeta ? [selectedMeta] : []) : modalMetas;

      if (!metas.length) {
        setActionError(
          modo === "item"
            ? "Selecione um item para movimentar."
            : "Não há itens disponíveis para movimentar."
        );
        return;
      }

      const menorSaldo = Math.min(...metas.map((meta) => meta.quantidade));
      const labelAcao = acao === "retornar" ? "Retornar" : "Realizar baixa";
      const labelModo =
        modo === "item" ? "item selecionado" : "todos os itens exibidos";

      setQuantityDialog({
        acao,
        modo,
        metas,
        titulo: `${labelAcao} ${labelModo}`,
        descricao:
          modo === "item"
            ? `${metas[0].codigo} — ${metas[0].nome}`
            : `${metas.length} itens serão movimentados com a mesma quantidade informada.`,
        quantidadeMaxima: menorSaldo,
      });

      setQuantityInput(String(menorSaldo));
      setQuantityError(null);
      setActionError(null);
    },
    [
      moduloAtual,
      permissoesStandby,
      podeBaixarReferenciaAberta,
      departamentoUsuario,
      selectedMeta,
      modalMetas,
    ]
  );

  const cancelarPopupQuantidade = useCallback(() => {
    if (submittingAction) return;

    setQuantityDialog(null);
    setQuantityInput("");
    setQuantityError(null);
  }, [submittingAction]);

  const confirmarMovimentacaoComQuantidade = useCallback(async () => {
    if (!quantityDialog) return;

    const quantidade = normalizarQuantidadeOperacao(quantityInput);

    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      setQuantityError("Informe uma quantidade inteira maior que zero.");
      return;
    }

    if (quantidade > quantityDialog.quantidadeMaxima) {
      setQuantityError(
        `A quantidade máxima permitida para esta operação é ${quantityDialog.quantidadeMaxima}.`
      );
      return;
    }

    setSubmittingAction(quantityDialog.acao);
    setQuantityError(null);
    setActionError(null);

    try {
      const payload = buildPayloadFromMetas({
        moduloAtual,
        tipo,
        acao: quantityDialog.acao,
        metas: quantityDialog.metas,
        quantidade,
      });

      await movimentarStandby(payload);
      await refresh();

      setSelectedIndex(null);
      setQuantityDialog(null);
      setQuantityInput("");
      setQuantityError(null);
      setActionError(null);

      if (quantityDialog.modo === "todos") {
        setOpenRef(null);
      }

      window.alert(
        quantityDialog.acao === "retornar"
          ? "Retorno registrado com sucesso."
          : "Baixa registrada com sucesso."
      );
    } catch (e: any) {
      setQuantityError(e?.message || "Falha ao movimentar standby.");
    } finally {
      setSubmittingAction(null);
    }
  }, [quantityDialog, quantityInput, moduloAtual, tipo, refresh]);

  const handleGerarRelatorioCompleto = useCallback(async () => {
    if (!openRef || !modalRawMovs.length) {
      window.alert(
        "Abra um standby com dados disponíveis para gerar o relatório."
      );
      return;
    }

    const materiais = modalRawMovs.flatMap((mov) =>
      (mov.itens || []).map((item) => ({
        codigo: item.produto?.codigo || "-",
        nome: item.produto?.nome || "-",
        quantidade: Number(item.quantidade ?? 0),
        custoUnitario: (() => {
          const n = Number(String(item.custo_unitario ?? "").replace(",", "."));
          return Number.isFinite(n) ? n : null;
        })(),
        tecnico:
          mov.movimentacao.responsavel_nome ||
          mov.movimentacao.coletor_nome ||
          "-",
      }))
    );

    const tecnicos = Array.from(
      new Set(
        modalRawMovs
          .map((mov) => mov.movimentacao.coletor_nome || "")
          .map((nome) => nome.trim())
          .filter(Boolean)
      )
    );

    try {
      await generatePdfReportAllOS({
        osNumero: openRef,
        tecnicos,
        materiais,
      });
    } catch (e: any) {
      window.alert(`Falha ao gerar relatório completo: ${e?.message || e}`);
    }
  }, [openRef, modalRawMovs]);

  const handleGerarRelatorioItem = useCallback(async () => {
    if (!openRef || selectedIndex == null) {
      window.alert("Selecione um item para gerar o relatório.");
      return;
    }

    const selected = filteredModalRows[selectedIndex];

    if (!selected) {
      window.alert("Item selecionado não encontrado.");
      return;
    }

    const meta = selected.meta;

    try {
      await generatePdfReport({
        osNumero: openRef,
        tecnicoNome:
          meta?.tecnicoNome || String(selected.row[IDX_SERVICO.TECNICO] ?? "-"),
        materiais: [
          {
            codigo:
              meta?.codigo ||
              String(
                tipo === "venda"
                  ? selected.row[IDX_VENDA.CODIGO]
                  : selected.row[IDX_SERVICO.CODIGO] ?? "-"
              ),
            nome:
              meta?.nome ||
              String(
                tipo === "venda"
                  ? selected.row[IDX_VENDA.NOME]
                  : selected.row[IDX_SERVICO.NOME] ?? "-"
              ),
            quantidade:
              meta?.quantidade ||
              Number(
                tipo === "venda"
                  ? selected.row[IDX_VENDA.QTD]
                  : selected.row[IDX_SERVICO.QTD] ?? 0
              ),
          },
        ],
      });
    } catch (e: any) {
      window.alert(`Falha ao gerar relatório do item: ${e?.message || e}`);
    }
  }, [openRef, selectedIndex, filteredModalRows, tipo]);

  const buttonBase: React.CSSProperties = {
    marginBottom: 8,
    padding: "11px 13px",
    borderRadius: 3,
    border: `1px solid ${PRIMARY_COLOR}`,
    background: "#ffffff",
    color: PRIMARY_COLOR,
    cursor: submittingAction ? "not-allowed" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Arial, sans-serif",
  };

  const buttonPrimary: React.CSSProperties = {
    ...buttonBase,
    background: PRIMARY_COLOR,
    color: "#ffffff",
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={pageTitle} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando standby…</div>
      ) : (
        <Table
          headers={headers}
          rows={displayRows}
          columnWidths={columnWidths}
          isTableStandby
          onDoubleClick={handleDoubleClick}
          atrasoColumnIndex={10}
          {...(deveAgruparMinhasOS
            ? {
                getRowGroup: (rowData: Row) => getRowGroupForTable(rowData),
                groupLabels: {
                  mine: "Minhas O.S.",
                  others: "O.S. de outros coordenadores",
                },
              }
            : {})}
          responsiveWidths={{
            1300: {
              2: "70px",
              3: "300px",
              4: "60px",
              5: "100px",
              6: "60px",
              7: "110px",
              8: "80px",
              9: "60px",
              10: "70px",
            },
          }}
        />
      )}

      <Modal open={!!openRef} onClose={closeModal} maxWidth={1280}>
        {openRef && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 10,
              width: "100%",
              position: "relative",
            }}
          >
            <div style={{ width: "100%" }}>
              <LeftTable data={modalInfo} />
            </div>

            {actionError && (
              <div
                style={{
                  padding: "9px 10px",
                  border: "1px solid #fecaca",
                  background: "#fef2f2",
                  color: "#991b1b",
                  fontSize: 13,
                  fontFamily: "Arial, sans-serif",
                }}
              >
                {actionError}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 2,
              }}
            >
              {permissoesStandby.podeGerarRelatorio && (
                <button
                  type="button"
                  disabled={!modalRawMovs.length || !!submittingAction}
                  onClick={handleGerarRelatorioCompleto}
                  style={{
                    ...buttonPrimary,
                    cursor:
                      !modalRawMovs.length || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity: !modalRawMovs.length || submittingAction ? 0.6 : 1,
                  }}
                >
                  {tipo === "venda"
                    ? "Gerar relatório da P.V."
                    : "Gerar relatório da O.S."}
                </button>
              )}

              {permissoesStandby.podeGerarRelatorio && (
                <button
                  type="button"
                  disabled={selectedIndex == null || !!submittingAction}
                  onClick={handleGerarRelatorioItem}
                  style={{
                    ...buttonBase,
                    cursor:
                      selectedIndex == null || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      selectedIndex == null || submittingAction ? 0.6 : 1,
                  }}
                >
                  Gerar relatório do item
                </button>
              )}

              {permissoesStandby.podeRetornar && (
                <button
                  type="button"
                  disabled={selectedIndex == null || !!submittingAction}
                  onClick={() => abrirPopupQuantidade("retornar", "item")}
                  style={{
                    ...buttonBase,
                    cursor:
                      selectedIndex == null || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      selectedIndex == null || submittingAction ? 0.6 : 1,
                  }}
                >
                  Retornar produto ao estoque
                </button>
              )}

              {podeBaixarReferenciaAberta && (
                <button
                  type="button"
                  disabled={!selectedMetaParaBaixa || !!submittingAction}
                  onClick={() => abrirPopupQuantidade("baixar", "item")}
                  style={{
                    ...buttonBase,
                    cursor:
                      !selectedMetaParaBaixa || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      !selectedMetaParaBaixa || submittingAction ? 0.6 : 1,
                  }}
                >
                  Realizar baixa do produto
                </button>
              )}

              {permissoesStandby.podeRetornar && (
                <button
                  type="button"
                  disabled={!modalMetas.length || !!submittingAction}
                  onClick={() => abrirPopupQuantidade("retornar", "todos")}
                  style={{
                    ...buttonBase,
                    cursor:
                      !modalMetas.length || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity: !modalMetas.length || submittingAction ? 0.6 : 1,
                  }}
                >
                  {tipo === "venda"
                    ? "Retornar P.V. ao estoque"
                    : "Retornar O.S. ao estoque"}
                </button>
              )}

              {podeBaixarReferenciaAberta && (
                <button
                  type="button"
                  disabled={!modalMetasParaBaixa.length || !!submittingAction}
                  onClick={() => abrirPopupQuantidade("baixar", "todos")}
                  style={{
                    ...buttonBase,
                    cursor:
                      !modalMetasParaBaixa.length || submittingAction
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      !modalMetasParaBaixa.length || submittingAction ? 0.6 : 1,
                  }}
                >
                  {tipo === "venda"
                    ? "Realizar baixa da P.V."
                    : "Realizar baixa da O.S."}
                </button>
              )}
            </div>

            <div
              style={{
                width: "100%",
                overflowX: "auto",
                fontFamily: "Arial, sans-serif",
                border: "1px solid #d7dee2",
              }}
            >
              <div style={{ minWidth: 1100 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: modalColWidths.join(" "),
                    fontWeight: 600,
                    padding: "10px 10px",
                    borderBottom: `1px solid ${PRIMARY_COLOR}`,
                    background: PRIMARY_COLOR,
                    color: "#ffffff",
                    fontFamily: "Arial, sans-serif",
                    fontSize: 12,
                  }}
                >
                  {modalHeaders.map((header, idx) => (
                    <div
                      key={header}
                      style={{
                        textAlign: centerColsModal.includes(idx)
                          ? "center"
                          : "left",
                      }}
                    >
                      {header}
                    </div>
                  ))}
                </div>

                {modalTableRows.map((row, i) => {
                  const selected = i === selectedIndex;

                  return (
                    <div
                      key={`${openRef}-${i}`}
                      onClick={() => {
                        setSelectedIndex(i);
                        setActionError(null);
                      }}
                      style={{
                        display: "grid",
                        gridTemplateColumns: modalColWidths.join(" "),
                        padding: "12px 10px",
                        borderBottom: "1px solid #e7ecef",
                        background: selected
                          ? "rgba(13, 52, 61, 0.08)"
                          : "#ffffff",
                        cursor: "pointer",
                        userSelect: "none",
                        fontFamily: "Arial, sans-serif",
                        fontSize: 12,
                      }}
                      title="Clique para selecionar"
                    >
                      {row.map((cell, j) => (
                        <div
                          key={j}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: centerColsModal.includes(j)
                              ? "center"
                              : "left",
                            color: selected ? PRIMARY_COLOR : "#263238",
                            fontWeight: selected ? 600 : 400,
                          }}
                          title={
                            typeof cell === "string" || typeof cell === "number"
                              ? String(cell)
                              : ""
                          }
                        >
                          {cell}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                marginTop: 4,
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                background: "#f9fafb",
                color: "#374151",
                fontSize: 12,
              }}
            >
              Para retorno ou baixa parcial, selecione o item e clique na ação.
              A quantidade será informada no pop-up de confirmação.
            </div>

            {quantityDialog && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  background: "rgba(15, 23, 42, 0.42)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 20,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    maxWidth: 440,
                    background: "#ffffff",
                    border: "1px solid #cfd8dc",
                    boxShadow: "0 18px 45px rgba(0,0,0,0.22)",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  <div
                    style={{
                      background: PRIMARY_COLOR,
                      color: "#ffffff",
                      padding: "14px 16px",
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    {quantityDialog.titulo}
                  </div>

                  <div
                    style={{
                      padding: 16,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        color: "#334155",
                        lineHeight: 1.45,
                      }}
                    >
                      {quantityDialog.descricao}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gap: 5,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 12,
                          color: PRIMARY_COLOR,
                          fontWeight: 700,
                        }}
                      >
                        Quantidade a movimentar
                      </label>

                      <input
                        autoFocus
                        type="number"
                        min="1"
                        step="1"
                        max={quantityDialog.quantidadeMaxima}
                        value={quantityInput}
                        onChange={(e) => {
                          setQuantityInput(e.target.value);
                          setQuantityError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            confirmarMovimentacaoComQuantidade();
                          }

                          if (e.key === "Escape") {
                            cancelarPopupQuantidade();
                          }
                        }}
                        style={{
                          height: 38,
                          border: "1px solid #b9c5ca",
                          outline: "none",
                          padding: "0 10px",
                          fontSize: 14,
                          color: "#111827",
                          boxSizing: "border-box",
                        }}
                      />

                      <div
                        style={{
                          fontSize: 11.5,
                          color: "#64748b",
                        }}
                      >
                        Máximo permitido:{" "}
                        <strong>{quantityDialog.quantidadeMaxima}</strong>
                      </div>
                    </div>

                    {quantityError && (
                      <div
                        style={{
                          padding: "8px 9px",
                          border: "1px solid #fecaca",
                          background: "#fef2f2",
                          color: "#991b1b",
                          fontSize: 12,
                        }}
                      >
                        {quantityError}
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 4,
                      }}
                    >
                      <button
                        type="button"
                        disabled={!!submittingAction}
                        onClick={cancelarPopupQuantidade}
                        style={{
                          padding: "10px 13px",
                          border: "1px solid #b9c5ca",
                          background: "#ffffff",
                          color: "#334155",
                          cursor: submittingAction ? "not-allowed" : "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Cancelar
                      </button>

                      <button
                        type="button"
                        disabled={!!submittingAction}
                        onClick={confirmarMovimentacaoComQuantidade}
                        style={{
                          padding: "10px 13px",
                          border: `1px solid ${PRIMARY_COLOR}`,
                          background: PRIMARY_COLOR,
                          color: "#ffffff",
                          cursor: submittingAction ? "not-allowed" : "pointer",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {submittingAction
                          ? "Registrando..."
                          : quantityDialog.acao === "retornar"
                          ? "Confirmar retorno"
                          : "Confirmar baixa"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StandbyGenerico;
